<script setup lang="ts">
import { computed } from 'vue'
import { PhBook } from '@phosphor-icons/vue'
import { Input } from '@/components/ui/input'
import { surahs } from '@/data/surahs'

const props = defineProps<{
  quranState: {
    navigationMode: 'surah' | 'verse' | 'juz'
    selectedSurah: number
    selectedJuz: number
    surahSearchQuery: string
    juzSearchQuery: string
  }
}>()

const emit = defineEmits<{
  'update:surahSearchQuery': [value: string]
  'update:juzSearchQuery': [value: string]
  navigateTo: [data: { mode: 'surah' | 'verse' | 'juz'; value: number; surah?: number; verse?: number }]
}>()

const filteredSurahs = computed(() => {
  if (!props.quranState.surahSearchQuery) return surahs
  const query = props.quranState.surahSearchQuery.toLowerCase()
  return surahs.filter(
    (s) =>
      s.englishName.toLowerCase().includes(query) ||
      s.englishNameTranslation.toLowerCase().includes(query) ||
      s.number.toString().includes(query),
  )
})

const filteredJuzs = computed(() => {
  const allJuzs = Array.from({ length: 30 }, (_, i) => i + 1)
  if (!props.quranState.juzSearchQuery) return allJuzs
  const query = props.quranState.juzSearchQuery.toLowerCase()
  return allJuzs.filter((juz) => juz.toString().includes(query) || `juz ${juz}`.toLowerCase().includes(query))
})
</script>

<template>
  <div v-if="quranState.navigationMode === 'surah'" class="group-data-[collapsible=icon]:hidden">
    <div class="px-3 py-3">
      <Input
        :model-value="quranState.surahSearchQuery"
        @update:model-value="(value: string | number) => emit('update:surahSearchQuery', String(value))"
        type="text"
        placeholder="Search surah by name or number..."
        class="w-full rounded-xl border border-moss-500/30 focus-visible:ring-moss-500"
      />
    </div>
    <div class="px-3 pb-2 space-y-1">
      <div
        v-for="surah in filteredSurahs"
        :key="surah.number"
        class="rounded-xl border border-transparent px-3 py-2 transition-all hover:border-moss-500/40 hover:bg-moss-500/8 cursor-pointer"
        :class="quranState.selectedSurah === surah.number ? 'border-moss-600 bg-moss-500/10' : ''"
        @click="emit('navigateTo', { mode: 'surah', value: surah.number })"
      >
        <div class="flex items-center justify-between gap-2">
          <div class="flex min-w-0 items-start gap-2.5">
            <PhBook :size="16" class="mt-0.5 shrink-0 text-primary/60" aria-hidden="true" />
            <div class="min-w-0 flex flex-col">
              <span class="text-sm font-semibold text-foreground">{{ surah.number }} · {{ surah.englishName }}</span>
              <span class="text-xs text-foreground/60">{{ surah.englishNameTranslation }}</span>
            </div>
          </div>
          <span class="shrink-0 text-xs font-medium text-foreground/60">{{ surah.numberOfAyahs }} ayat</span>
        </div>
      </div>
    </div>
  </div>

  <div v-else-if="quranState.navigationMode === 'juz'" class="group-data-[collapsible=icon]:hidden">
    <div class="px-3 py-3">
      <Input
        :model-value="quranState.juzSearchQuery"
        @update:model-value="(value: string | number) => emit('update:juzSearchQuery', String(value))"
        type="text"
        placeholder="Find a juz by name or number..."
        class="w-full rounded-xl border border-moss-500/30 focus-visible:ring-moss-500"
      />
    </div>
    <div class="px-3 pb-2 grid grid-cols-2 gap-2">
      <button
        v-for="juz in filteredJuzs"
        :key="juz"
        type="button"
        :class="[
          'rounded-xl border px-3 py-3 text-sm font-semibold transition-all',
          quranState.selectedJuz === juz
            ? 'bg-[#046A38] text-bullion-50 border-[#046A38]'
            : 'border-moss-500/30 hover:border-moss-500/60 hover:bg-moss-500/10 text-foreground',
        ]"
        @click="emit('navigateTo', { mode: 'juz', value: juz })"
      >
        Juz {{ juz }}
      </button>
    </div>
  </div>
</template>
